import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import OpalPresentationTable from '../DenaliAnalysis/tables/presentation'
import FlexColumn from '../../molecules/FlexColumn'
import useBlueprintData from './hooks/useBlueprintData'
import useBlueprintColumnDefs, { mapDefaultSort } from './hooks/useBlueprintColumnDefs'
import BlueprintExportButton from './BlueprintExportButton'

/**
 * Blueprint-driven presentation table. Receives its full configuration
 * from the blueprint definition — no manual column or sort config needed.
 */
function BlueprintPresentationTable ({ blueprintCode, config, parameters }) {
  const {
    purposeCode,
    footerPurposeCode,
    footerColumnMapping,
    defaultSort: rawDefaultSort
  } = config

  const hasFooterPurpose = !!footerPurposeCode && footerPurposeCode !== purposeCode

  const { data: mainData, isLoading: mainLoading } = useBlueprintData(
    blueprintCode,
    purposeCode,
    { parameters, pageSize: 1000 }
  )

  const { data: footerData, isLoading: footerLoading } = useBlueprintData(
    blueprintCode,
    hasFooterPurpose ? footerPurposeCode : null,
    { parameters, pageSize: 1000 }
  )

  const { columns, includeFooter } = useBlueprintColumnDefs(
    config.columns,
    hasFooterPurpose ? footerData : [],
    footerColumnMapping
  )

  const defaultSort = useMemo(() => mapDefaultSort(rawDefaultSort), [rawDefaultSort])

  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.defaultShown ?? true)
  }, [columns])

  const isLoading = mainLoading || (hasFooterPurpose && footerLoading)

  const showToolbar = !!config.export?.enabled

  const table = (
    <OpalPresentationTable
      data={mainData || []}
      columns={visibleColumns}
      defaultSort={defaultSort}
      includeHeader
      includeFooter={includeFooter}
      isLoading={isLoading}
      emptyStateText='No Data Available'
    />
  )

  if (!showToolbar) return table

  return (
    <FlexColumn className='gap-xs'>
      <div className='flex flex-row justify-end'>
        {/* filters, subordinatePredicates, sortBy, sortDirection are intentionally
           omitted -- PresentationTable has no user-facing filter or sort controls,
           so the export always reflects the base query with parameters only. */}
        <BlueprintExportButton
          blueprintCode={blueprintCode}
          config={config}
          purposeCode={purposeCode}
          parameters={parameters}
        />
      </div>
      {table}
    </FlexColumn>
  )
}

BlueprintPresentationTable.propTypes = {
  /** The blueprint code (used for data fetching) */
  blueprintCode: PropTypes.string.isRequired,

  /** The resolved presentation config from the blueprint definition */
  config: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      hidden: PropTypes.bool,
      exclude: PropTypes.bool,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      formatString: PropTypes.string
    })),
    defaultSort: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      dir: PropTypes.string
    })),
    purposeCode: PropTypes.string.isRequired,
    localStorageKey: PropTypes.string,
    footerPurposeCode: PropTypes.string,
    footerColumnMapping: PropTypes.arrayOf(PropTypes.shape({
      mainColumn: PropTypes.string,
      footerColumn: PropTypes.string
    })),
    variationGroups: PropTypes.arrayOf(PropTypes.string),
    export: PropTypes.shape({
      enabled: PropTypes.bool,
      formats: PropTypes.arrayOf(PropTypes.string),
      defaultFileName: PropTypes.string
    })
  }).isRequired,

  /** Runtime parameters for variable substitution */
  parameters: PropTypes.object
}

BlueprintPresentationTable.defaultProps = {
  parameters: {}
}

export default BlueprintPresentationTable
